import React, { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { toast as message } from 'sonner';
import { useFieldDependentTable } from '../../../utils/metadata/tables';
import { useGlobalMetadata } from '../../../utils/providers';
import { useMyCapabilities } from '../../../utils/metadata/capabilities';
import { insertRecord, deleteRecord, updateRecord, handleOperationError } from '../../../utils/supabase-operations';
import { invalidateAndRefreshAllQueriesForTable } from '../../../utils/metadata';
import { useEditLocks } from '../../../hooks/useEditLocks';
import RecordDetailForm from '../../RecordDetailForm';
import RecordLookupSelect from '../../RecordLookupSelect';
import RecordHistory from '../../RecordHistory';
import { SubmissionPrimaryApprovalActions } from '../../PrimaryApprovalActions';
import StatusPill from '../merchant/atoms/StatusPill';
import { buildSubmissionSections, defaultOpenSections } from './submissionLayout';
import type { PanelRecord } from '../SidePanelContext';
import type { RecordDetailFormHandle } from '../../../types/record-form';
import type { RelationshipLookup } from '../../../types/metadata';
import type { BaseRecord } from '../../../types/api';
import './SubmissionPanelBody.css';

/**
 * Synthetic relationship metadata for the rep search select on the
 * Rep Commission card. No relationship_lookup row is registered for
 * rep_link.rep (it's not exposed as a generic form field) — RecordLookupSelect
 * only reads target_table_name/target_label_field, so a client-side object is
 * enough without touching core_crm metadata.
 */
const REP_RELATIONSHIP: RelationshipLookup = {
  id: 0,
  col_name: 'rep',
  source_table_name: 'rep_link',
  source_schema_name: 'public',
  target_table_name: 'rep',
  target_schema_name: 'public',
  target_label_field: 'name',
  cascade_delete: false,
  expand_fields: false,
  expand_prefix: null,
  foreign_key_name: '',
  created_at: '',
  updated_at: '',
  created_by: 0,
  updated_by: 0,
};

interface SubmissionPanelBodyProps {
  record: PanelRecord;
  /** Dismiss the surrounding modal — wired to Cancel and to a successful delete. */
  onClose?: () => void;
}

interface RepLinkRow {
  id: number;
  rep: number;
  merchant: number;
  submission: number | null;
  commission_percentage: number | null;
  date_rep_paid: string | null;
  rep_rel?: { name?: string } | null;
}

function SubmissionPanelBody({ record, onClose }: SubmissionPanelBodyProps) {
  const { data: metadata } = useGlobalMetadata();
  const formRef = useRef<RecordDetailFormHandle>(null);
  const [saving, setSaving] = useState(false);
  // The staged (unsaved) status edit. Sections react to it so the
  // missing-documents field appears the moment the dropdown changes,
  // before the record is saved.
  const [draftStatus, setDraftStatus] = useState<string | null>(null);

  const tableInfo = metadata?.tableData?.byName?.['submission'];
  const schemaName = tableInfo?.schema_name || 'public';
  const submissionId = Number(record.id);
  // Same gate RecordDeleteButton applies — this footer replaces that button, so
  // it has to honour a table whose metadata turns deletion off.
  const canDelete = (tableInfo?.ui_data as { show_delete?: boolean } | undefined)?.show_delete ?? true;

  const conditions = useMemo(() => ({
    id: {
      filterType: 'number' as const,
      type: 'equals' as const,
      filter: submissionId,
      colId: 'id',
    },
  }), [submissionId]);

  const { data: submission, isLoading } = useFieldDependentTable({
    enabled: !!record.id,
    tableName: 'submission',
    schema: schemaName,
    deep: true,
    conditions,
    limitOffset: { limit: 1, offset: 0 },
    withCount: false,
    singleRow: true,
  });

  const s = submission as Record<string, any> | null;

  const repLinkConditions = useMemo(() => ({
    submission: { filterType: 'number' as const, type: 'equals' as const, filter: submissionId, colId: 'submission' },
  }), [submissionId]);

  const { data: links } = useFieldDependentTable({
    enabled: !!submissionId,
    tableName: 'rep_link',
    schema: 'public',
    deep: true,
    conditions: repLinkConditions,
  });

  const repLinks = useMemo(
    () => (Array.isArray(links) ? links : links ? [links] : []) as unknown as RepLinkRow[],
    [links],
  );

  // The commission pool the splits divide up. `commission_calc` is the
  // database's own number, so the card agrees with the APPROVAL section above it.
  const commissionPool = Number(s?.commission_calc ?? 0) || 0;

  const sections = useMemo(() => {
    const savedStatus = s?.status || '';
    const status = draftStatus ?? savedStatus;
    // The open map follows the saved record — a draft status change should
    // reveal fields, not collapse or expand sections the user is looking at.
    const openMap = defaultOpenSections(savedStatus, repLinks.length);
    return buildSubmissionSections(metadata?.fieldData, openMap, {
      status,
      repCount: repLinks.length,
      commissionPool,
      hasDeclineData: !!(s?.reason_for_decline || s?.declined_date),
      hasMissingDocs: !!s?.missing_documents,
    });
  }, [
    commissionPool, draftStatus, metadata?.fieldData, repLinks.length,
    s?.status, s?.reason_for_decline, s?.declined_date, s?.missing_documents,
  ]);

  const tabConfig = useMemo(() => ({
    table_name: 'submission',
    schema_name: schemaName,
    sections,
  }), [schemaName, sections]);

  const sectionExtras = useMemo(() => ({
    'REP COMMISSION': (
      <RepCommissionEditor
        submissionId={submissionId}
        merchantId={Number(s?.merchant)}
        rows={repLinks}
        commissionPool={commissionPool}
      />
    ),
    // The change log used to be a sibling tab. It belongs with the audit
    // columns, and only mounts when that section is opened — it is the most
    // expensive query on this record.
    AUDIT: <RecordHistory tableName="submission" recordId={submissionId} />,
  }), [commissionPool, repLinks, s?.merchant, submissionId]);

  const handleSave = useCallback(async () => {
    if (!formRef.current) return;
    setSaving(true);
    try {
      await formRef.current.save();
    } finally {
      setSaving(false);
    }
  }, []);

  const handleCancel = useCallback(() => {
    formRef.current?.discard();
    setDraftStatus(null);
    onClose?.();
  }, [onClose]);

  const handleDraftChange = useCallback((fieldName: string, value: unknown) => {
    if (fieldName === 'status') setDraftStatus(typeof value === 'string' ? value : null);
  }, []);

  if (isLoading) {
    return <div className="sky-mp-empty">Loading...</div>;
  }

  return (
    <div className="sky-sub-detail" data-testid="record-detail" aria-busy={isLoading}>
      <RecordDetailForm
        ref={formRef}
        key={`submission::${record.id}`}
        tabConfig={tabConfig}
        recordId={submissionId}
        manualSave
        hideChrome
        sectionVariant="cards"
        sectionExtras={sectionExtras}
        onDraftChange={handleDraftChange}
        onDeleted={onClose}
      />

      <div className="sky-sub-detail-footer">
        {canDelete ? (
          <button
            type="button"
            className="sky-btn sky-btn-danger-ghost"
            disabled={saving}
            onClick={() => formRef.current?.requestDelete()}
          >
            Delete Submission
          </button>
        ) : <span />}
        <div className="sky-sub-detail-footer-right">
          <SubmissionPrimaryApprovalActions
            submissionId={submissionId}
            merchantId={Number(s?.merchant) || 0}
            status={s?.status ?? null}
          />
          <button type="button" className="sky-btn sky-btn-ghost" disabled={saving} onClick={handleCancel}>
            Cancel
          </button>
          <button type="button" className="sky-btn sky-btn-primary" disabled={saving} onClick={() => void handleSave()}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal header ────────────────────────────────────────────────────────
 * Rendered by LinkedRecordPanel inside the modal's own dark header, so a
 * submission gets one header instead of a generic bar stacked on a summary
 * card. Carries what a user checks before touching a deal: which submission it
 * is, whose it is, who the merchant is, and the number to call them on.
 *
 * The submission query here is the same one the body runs, so React Query
 * serves both from a single request. */

export function SubmissionPanelHeader({ recordId }: { recordId: string | number }) {
  const { data: metadata } = useGlobalMetadata();
  const schemaName = metadata?.tableData?.byName?.['submission']?.schema_name || 'public';
  const submissionId = Number(recordId);

  const conditions = useMemo(() => ({
    id: { filterType: 'number' as const, type: 'equals' as const, filter: submissionId, colId: 'id' },
  }), [submissionId]);

  const { data: submission } = useFieldDependentTable({
    enabled: submissionId > 0,
    tableName: 'submission',
    schema: schemaName,
    deep: true,
    conditions,
    limitOffset: { limit: 1, offset: 0 },
    withCount: false,
    singleRow: true,
  });

  const s = (submission as Record<string, any> | null) || null;
  const merchantId = Number(s?.merchant) || 0;

  const merchantConditions = useMemo(() => ({
    id: { filterType: 'number' as const, type: 'equals' as const, filter: merchantId, colId: 'id' },
  }), [merchantId]);

  // The submission's `merchant_rel` carries a display label only; the contact
  // name and phone shown here live on the merchant row itself.
  const { data: merchant } = useFieldDependentTable({
    enabled: merchantId > 0,
    tableName: 'merchant',
    schema: 'public',
    deep: false,
    conditions: merchantConditions,
    limitOffset: { limit: 1, offset: 0 },
    withCount: false,
    singleRow: true,
  });

  const m = merchant as Record<string, any> | null;
  const contact = [m?.first_name, m?.last_name].filter(Boolean).join(' ');
  const business = relName(s?.merchant_rel) || m?.business_name || '';
  const status = s?.status || '';
  const number = s?.submission_number || submissionId;

  return (
    <div className="sky-sub-header">
      <div className="sky-sub-header-title">
        <span className="sky-sub-header-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6M9 13h6M9 17h4" />
          </svg>
        </span>
        <span className="sky-sub-header-eyebrow">Submission</span>
        <span className="sky-sub-header-number">#{number}</span>
        {status && <StatusPill status={mapStatus(status)} label={statusLabel(status)} />}
      </div>

      <div className="sky-sub-header-business">
        {business || `Merchant #${merchantId}`}
        {merchantId > 0 && <span className="sky-sub-header-merchant"> • Merchant #{merchantId}</span>}
      </div>

      <div className="sky-sub-header-grid">
        <IdentityCell label="Submission #" value={`#${number}`} />
        <IdentityCell label="Main Rep" value={relName(s?.main_rep_rel)} />
        <IdentityCell label="Merchant Name" value={contact} />
        <IdentityCell label="Phone" value={m?.phone || ''} />
      </div>
    </div>
  );
}

function IdentityCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="sky-sub-identity-cell">
      <span className="sky-sub-identity-label">{label}</span>
      <span className="sky-sub-identity-value">{value || '—'}</span>
    </div>
  );
}

/* ── Rep commission splits (public.rep_link) ─────────────────────────────
 * This is also the write side of Closer row-scoping: a Closer sees a deal
 * because they appear here (main_rep OR rep_link), so this card is the
 * day-to-day way to grant that visibility instead of writing rep_link rows by
 * hand in the database. Gated on the edit_deals capability, matching rep_link's
 * RLS (supabase/pg/rbac/deal_rls.sql). */

function RepCommissionEditor({
  submissionId,
  merchantId,
  rows,
  commissionPool,
}: {
  submissionId: number;
  merchantId: number;
  rows: RepLinkRow[];
  commissionPool: number;
}) {
  const { hasCapability } = useMyCapabilities();
  const canEdit = hasCapability('edit_deals');
  const [adding, setAdding] = useState(false);
  const [selectedRep, setSelectedRep] = useState<BaseRecord | null>(null);
  const [commissionPct, setCommissionPct] = useState('');
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [splitting, setSplitting] = useState(false);

  // Commission is edited in place with no edit mode, so the lock follows focus — same
  // treatment the generic detail form gives its fields.
  const [, bumpLocks] = useReducer((n: number) => n + 1, 0);
  const { getLockOwner, acquire, release } = useEditLocks({
    schema: 'public',
    tableName: 'rep_link',
    onChange: bumpLocks,
  });

  const allocated = rows.reduce((total, row) => total + (Number(row.commission_percentage) || 0), 0);

  const resetForm = useCallback(() => {
    setAdding(false);
    setSelectedRep(null);
    setCommissionPct('');
  }, []);

  const handleAdd = useCallback(async () => {
    if (!selectedRep) return;
    setSaving(true);
    try {
      const { error } = await insertRecord({
        tableName: 'rep_link',
        schema: 'public',
        data: {
          rep: selectedRep.id,
          merchant: merchantId,
          submission: submissionId,
          commission_percentage: commissionPct ? Number(commissionPct) : null,
        },
        options: { invalidateCache: false },
      });
      if (error) throw error;
      await invalidateAndRefreshAllQueriesForTable('rep_link');
      message.success('Rep added');
      resetForm();
    } catch (error) {
      const info = handleOperationError(error, 'Failed to add rep');
      message.error(info.userMessage);
    } finally {
      setSaving(false);
    }
  }, [selectedRep, merchantId, submissionId, commissionPct, resetForm]);

  const handleRemove = useCallback(async (linkId: number) => {
    setRemovingId(linkId);
    try {
      const { error } = await deleteRecord({
        tableName: 'rep_link',
        schema: 'public',
        whereClause: { id: linkId },
        options: { invalidateCache: false },
      });
      if (error) throw error;
      await invalidateAndRefreshAllQueriesForTable('rep_link');
    } catch (error) {
      const info = handleOperationError(error, 'Failed to remove rep');
      message.error(info.userMessage);
    } finally {
      setRemovingId(null);
    }
  }, []);

  const patchLink = useCallback(async (
    linkId: number,
    field: 'commission_percentage' | 'date_rep_paid',
    value: string | number | null,
    previousValue: string | number | null,
  ) => {
    if (value === previousValue) return;
    try {
      const result = await updateRecord({
        tableName: 'rep_link',
        schema: 'public',
        data: { [field]: value },
        whereClause: { id: linkId },
        options: {
          invalidateCache: false,
          fieldName: field,
          expectedValues: { [field]: previousValue },
          expectedValueTypes: { [field]: field === 'date_rep_paid' ? 'date' : 'numeric' },
        },
      });
      if (result.error) throw result.error;
      await invalidateAndRefreshAllQueriesForTable('rep_link');
    } catch (error) {
      const info = handleOperationError(error, 'Failed to update commission');
      message.error(info.userMessage);
    }
  }, []);

  /**
   * Divide the pool evenly, giving the remainder to the first rep so the
   * percentages still total exactly 100 rather than 99.99.
   */
  const handleSplitEvenly = useCallback(async () => {
    if (rows.length === 0) return;
    setSplitting(true);
    try {
      const even = Math.floor((100 / rows.length) * 100) / 100;
      const remainder = Math.round((100 - even * rows.length) * 100) / 100;
      for (const [index, row] of rows.entries()) {
        const share = index === 0 ? Math.round((even + remainder) * 100) / 100 : even;
        if (share === row.commission_percentage) continue;
        const result = await updateRecord({
          tableName: 'rep_link',
          schema: 'public',
          data: { commission_percentage: share },
          whereClause: { id: row.id },
          options: {
            invalidateCache: false,
            fieldName: 'commission_percentage',
            expectedValues: { commission_percentage: row.commission_percentage },
            expectedValueTypes: { commission_percentage: 'numeric' },
          },
        });
        if (result.error) throw result.error;
      }
      await invalidateAndRefreshAllQueriesForTable('rep_link');
    } catch (error) {
      const info = handleOperationError(error, 'Failed to split commission');
      message.error(info.userMessage);
    } finally {
      setSplitting(false);
    }
  }, [rows]);

  const lockOwner = (linkId: number, field: string): string | null => getLockOwner(linkId, field);

  return (
    <div className="sky-rep-split">
      {rows.length === 0 && !adding && (
        <div className="sky-mp-empty-inline">No commission reps on this deal.</div>
      )}

      {rows.length > 0 && (
        <div className="sky-rep-split-table">
          <div className="sky-rep-split-head">
            <span>Rep</span>
            <span>Commission %</span>
            <span>Commission $</span>
            <span>Date Rep Paid</span>
            <span />
          </div>
          {rows.map((row) => {
            const pct = Number(row.commission_percentage) || 0;
            return (
              <div key={row.id} className="sky-rep-split-row">
                <span className="sky-rep-split-name">{row.rep_rel?.name || `Rep #${row.rep}`}</span>
                {canEdit ? (
                  <input
                    className={`sky-field-input${lockOwner(row.id, 'commission_percentage') ? ' live-cell-editing' : ''}`}
                    type="number"
                    step="0.01"
                    placeholder="%"
                    title={lockOwner(row.id, 'commission_percentage') ? `${lockOwner(row.id, 'commission_percentage')} is editing this` : undefined}
                    readOnly={Boolean(lockOwner(row.id, 'commission_percentage'))}
                    defaultValue={row.commission_percentage ?? ''}
                    onFocus={() => { if (!lockOwner(row.id, 'commission_percentage')) acquire(row.id, 'commission_percentage'); }}
                    onBlur={(e) => {
                      release(row.id, 'commission_percentage');
                      void patchLink(row.id, 'commission_percentage', e.target.value === '' ? null : Number(e.target.value), row.commission_percentage);
                    }}
                  />
                ) : (
                  <span className="sky-rep-split-static">{pct ? `${pct}%` : '—'}</span>
                )}
                <span className="sky-rep-split-static sky-rep-split-derived">
                  {commissionPool ? fmtMoney(commissionPool * pct / 100) : '—'}
                </span>
                {canEdit ? (
                  <input
                    className={`sky-field-input${lockOwner(row.id, 'date_rep_paid') ? ' live-cell-editing' : ''}`}
                    type="date"
                    readOnly={Boolean(lockOwner(row.id, 'date_rep_paid'))}
                    defaultValue={row.date_rep_paid ? String(row.date_rep_paid).slice(0, 10) : ''}
                    onFocus={() => { if (!lockOwner(row.id, 'date_rep_paid')) acquire(row.id, 'date_rep_paid'); }}
                    onBlur={(e) => {
                      release(row.id, 'date_rep_paid');
                      void patchLink(row.id, 'date_rep_paid', e.target.value || null, row.date_rep_paid);
                    }}
                  />
                ) : (
                  <span className="sky-rep-split-static">{row.date_rep_paid ? String(row.date_rep_paid).slice(0, 10) : '—'}</span>
                )}
                {canEdit ? (
                  <button
                    className="sky-icon-btn"
                    title="Remove rep"
                    aria-label={`Remove ${row.rep_rel?.name || `rep #${row.rep}`}`}
                    disabled={removingId === row.id}
                    onClick={() => void handleRemove(row.id)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                ) : <span />}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && (
        <div className="sky-rep-split-actions">
          {!adding && <button type="button" className="sky-rep-split-link" onClick={() => setAdding(true)}>+ Add rep</button>}
          {rows.length > 1 && (
            <button type="button" className="sky-rep-split-link" disabled={splitting} onClick={() => void handleSplitEvenly()}>
              {splitting ? 'Splitting…' : 'Split evenly'}
            </button>
          )}
          {rows.length > 0 && (
            <span className={`sky-rep-split-total${Math.abs(allocated - 100) < 0.01 ? ' is-complete' : ''}`}>
              {allocated % 1 === 0 ? allocated : allocated.toFixed(2)}% allocated
            </span>
          )}
        </div>
      )}

      {adding && (
        <div className="sky-mp-new-form" style={{ marginTop: 10 }}>
          <div className="sky-mp-new-form-row">
            <label className="sky-mp-new-form-label">
              Rep
              <RecordLookupSelect
                name="rep"
                relationship={REP_RELATIONSHIP}
                record={selectedRep}
                schema="public"
                onChange={setSelectedRep}
              />
            </label>
          </div>
          <div className="sky-mp-new-form-row">
            <label className="sky-mp-new-form-label">
              Commission %
              <input
                className="sky-mp-new-form-input"
                type="number"
                step="0.01"
                value={commissionPct}
                onChange={(e) => setCommissionPct(e.target.value)}
              />
            </label>
          </div>
          <div className="sky-mp-new-form-actions">
            <button className="sky-mp-new-form-save" disabled={!selectedRep || saving} onClick={() => void handleAdd()}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="sky-mp-new-form-cancel" disabled={saving} onClick={resetForm}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Helpers ────────────────────────────────────────────── */

function relName(rel: any): string {
  if (!rel) return '';
  if (typeof rel === 'string') return rel;
  return rel.display_name || rel.name || rel.full_name || rel.dba_name || rel.label || '';
}

function mapStatus(s: string): string {
  const lower = (s || '').toLowerCase();
  if (lower.includes('approved')) return 'approved';
  if (lower.includes('funded')) return 'funded';
  if (lower.includes('declined') || lower.includes('dead')) return 'declined';
  if (lower.includes('missing')) return 'missing';
  if (lower.includes('contract')) return 'contracts';
  if (lower.includes('submitted') || lower.includes('new')) return 'submitted';
  return '';
}

function statusLabel(s: string): string {
  const lower = (s || '').toLowerCase();
  if (lower.includes('approved')) return 'Approved';
  if (lower.includes('funded')) return 'Funded';
  if (lower.includes('declined')) return 'Declined';
  if (lower.includes('missing')) return 'Missing Doc';
  // Contracts In and Contracts Out share a pill colour but are different stages.
  if (lower.includes('contracts in')) return 'Contracts In';
  if (lower.includes('contract')) return 'Contracts Out';
  return s;
}

function fmtMoney(v: number): string {
  if (!v) return '—';
  return '$' + Math.round(v).toLocaleString('en-US');
}

export default React.memo(SubmissionPanelBody);
